import React, { useState } from "react";
import { Modal, Button, Form, ListGroup } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  ReportsApi,
  ServerTransferSavedReportResponse,
} from "../shared/api/axios-client";
import { createApiService } from "../shared/apiConfig";
import { Pencil, Trash2 } from "react-bootstrap-icons";
import { showDate } from "../Data/Date";
import { LocalDate } from "@js-joda/core";

interface Props {
  show: boolean;
  onClose: () => void;
  savedReports: ServerTransferSavedReportResponse[];
  setSavedReports: React.Dispatch<
    React.SetStateAction<ServerTransferSavedReportResponse[]>
  >;
  onRun: (filters: any) => void;
  currentFacilityIds?: number[];
  facilities?: Array<{ facilityId: number; name: string }>;
  reportType: string;
}

export function ManageSavedReportsModal({
  show,
  onClose,
  savedReports,
  setSavedReports,
  onRun,
  currentFacilityIds,
  facilities,
  reportType
}: Props) {
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editedName, setEditedName] = useState("");
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isReportRecurring, setIsReportRecurring] = useState<boolean>(false);

  const reportsApi = createApiService(ReportsApi);

  const ignoredKeys = ["page", "pageSize", "facilityId", "quickDateMode", "quickPayoutDateMode"];
  const dateFields = ['from', 'to', 'payoutFrom', 'payoutTo'];

  const getFacilityNames = (facilityIds: number[]) => {
    if (!facilities || !facilityIds) return facilityIds;
    return facilityIds
      .map(id => facilities.find(f => f.facilityId === id)?.name || `Facility ${id}`)
      .join(', ');
  };

  const handleRename = async (index: number) => {
    const trimmed = editedName.trim();
    if (!trimmed) {
      toast.error("Name cannot be empty");
      return;
    }

    const reportToRename = savedReports[index];
    if (!reportToRename) return;

    const duplicate = savedReports.some(
      (r, i) => i !== index && r.name === trimmed
    );
    if (duplicate) {
      toast.error("A report with this name already exists");
      return;
    }

    await reportsApi.monitorReportsRenameSavedReportPost({
      savedReportId: reportToRename.savedReportId,
      newName: trimmed,
    });

    const updated = [...savedReports];
    updated[index].name = trimmed;
    setSavedReports(updated);
    setEditIndex(null);
    toast.success("Report renamed successfully");
  };

  const confirmDelete = async () => {
    if (deleteIndex === null) return;

    const reportToDelete = savedReports[deleteIndex];
    if (!reportToDelete) return;

    await reportsApi.monitorReportsSavedReportDelete({
      savedReportId: reportToDelete.savedReportId,
    });

    const updated = [...savedReports];
    updated.splice(deleteIndex, 1);
    setSavedReports(updated);
    toast.success("Report removed successfully");
    setDeleteIndex(null);
    setIsReportRecurring(false);
  };

  const selectFieldLabelMaps: Record<string, Record<string, string>> = {
    paymentStatus: {
      cleared: "Cleared",
      failed: "ACH Failure",
      reversed: "Reversed",
      processing: "Processing",
    },
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Manage Saved Reports</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {savedReports.length === 0 ? (
          <p className="text-muted">No saved reports.</p>
        ) : (
          <ListGroup>
            {savedReports.map((report, index) => (
              <ListGroup.Item key={index} className="pb-3">
                {editIndex === index ? (
                  <Form.Control
                    size="sm"
                    value={editedName ?? report.name}
                    onChange={(e) => setEditedName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRename(index);
                    }}
                    autoFocus
                    className="mb-2"
                  />
                ) : (
                  <div className="fw-semibold d-flex justify-content-between align-items-center mb-2">
                    {report.name}
                    <div className="d-flex gap-2">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => {
                          setEditedName(report.name || '');
                          setEditIndex(index);
                        }}
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => {setDeleteIndex(index); setIsReportRecurring(report.isRecurring!);}}
                      >
                        <Trash2 size={16} color="red" />
                      </Button>
                    </div>
                  </div>
                )}
                <div className="small text-muted mb-3 p-3" style={{ border: '1px solid #dee2e6', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <div className="d-flex flex-column">
                  {(() => {
                    try {
                      const filterData = report.filterJson ? JSON.parse(report.filterJson) : {};
                      return Object.entries(filterData)
                      .filter(([key, value]) => {
                        const isDateField = dateFields.includes(key);
                        if (isDateField && (key === 'from' || key === 'to') && filterData.quickDateMode !== 'Custom')
                          return false;
                        
                        if (isDateField && (key === 'payoutFrom' || key === 'payoutTo') && filterData.quickPayoutDateMode !== 'Custom')
                          return false;

                        return !!value && !ignoredKeys.includes(key);
                      })

                    } catch (error) {
                      console.error('Error parsing filter JSON:', error);
                      return [];
                    }
                  })()
                    .map(([key, value]) => (
                      <div key={key} className="d-flex mb-1">
                        <div style={{ width: "130px", fontWeight: 500 }}>
                          {key
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())}
                          :
                        </div>
                        <div className="text-break">
                        {key === "facilityIds" 
                          ? getFacilityNames(value as number[])
                          : key.toLowerCase().includes("date") || dateFields.includes(key)
                            ? typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)
                              ? showDate(LocalDate.parse(value.substring(0, 10)))
                              : String(value) 
                            : selectFieldLabelMaps[key]?.[value as string] ?? String(value)}
                        </div>
                      </div>
                    ))}
                  {reportType != 'Aging Report' && (() => {
                    try {
                      const filterData = report.filterJson ? JSON.parse(report.filterJson) : {};
                      return filterData.quickDateMode && filterData.quickDateMode !== 'Custom';
                    } catch (error) {
                      return false;
                    }
                  })() && (
                    <div className="d-flex mb-1">
                      <div style={{ width: "130px", fontWeight: 500 }}>Payment Date:</div>
                      <div className="text-break">{(() => {
                        try {
                          const filterData = report.filterJson ? JSON.parse(report.filterJson) : {};
                          return filterData.quickDateMode;
                        } catch (error) {
                          return '';
                        }
                      })()}</div>
                    </div>
                  )}
                   {reportType != 'Aging Report' && (() => {
                    try {
                      const filterData = report.filterJson ? JSON.parse(report.filterJson) : {};
                      return filterData.quickPayoutDateMode && filterData.quickPayoutDateMode !== 'Custom';
                    } catch (error) {
                      return false;
                    }
                  })() && (
                    <div className="d-flex mb-1">
                      <div style={{ width: "130px", fontWeight: 500 }}>Payout Date:</div>
                      <div className="text-break">{(() => {
                        try {
                          const filterData = report.filterJson ? JSON.parse(report.filterJson) : {};
                          return filterData.quickPayoutDateMode;
                        } catch (error) {
                          return '';
                        }
                      })()}</div>
                    </div>
                  )}
                  </div>
                </div>

                {editIndex === index ? (
                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => handleRename(index)}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setEditIndex(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="d-flex justify-content-start mt-2">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => {
                        try {
                          const filterData = report.filterJson ? JSON.parse(report.filterJson) : {};
                          // Include current facilityIds if they exist and the saved report doesn't have facilityIds
                          if (currentFacilityIds && currentFacilityIds.length > 0 && !filterData.facilityIds) {
                            filterData.facilityIds = currentFacilityIds;
                          }
                          onRun(filterData);
                          onClose();
                        } catch (error) {
                          console.error('Error parsing filter JSON:', error);
                          toast.error('Error loading report filters');
                        }
                      }}
                    >
                      Run
                    </Button>
                  </div>
                )}
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => {
            onClose();
            setEditIndex(null);
          }}
        >
          Close
        </Button>
      </Modal.Footer>

      <Modal
        show={deleteIndex !== null}
        onHide={() => {setDeleteIndex(null); setIsReportRecurring(false)}}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>Are you sure you want to delete this saved report?</p>

          {isReportRecurring && (
            <div className="alert alert-warning mt-3">
              <strong>⚠️ This report is part of a recurring schedule.</strong><br />
              Deleting it will also remove the associated recurring report schedule.
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => {setDeleteIndex(null); setIsReportRecurring(false)}}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
}
